import type { Product } from "../types/Product";
import type { StoreProductResult } from "../services/ProductSearchService";
import { getProductUrl as getRetailerProductUrl } from "./ProductItem.utils";
import { getLatestValue, getDiscountPercent } from "./ProductItem.utils";
import { PUBLIC_DEALS_RETAILERS } from "../config/publicDeals";
import { isOnlineOnlyRetailer } from "../config/retailers";

const PUBLIC_ONLINE_DETAIL_RETAILERS = new Set(
  PUBLIC_DEALS_RETAILERS.map((retailer) => retailer.apiRetailer)
);
export const isPublicOnlineProductContext = (
  retailer?: string | null,
  storeId?: string | null
) => {
  const normalizedRetailer = retailer?.trim().toLowerCase() || "";
  if (
    !PUBLIC_ONLINE_DETAIL_RETAILERS.has(
      normalizedRetailer
        .replace("walmart_online", "walmart")
        .replace("target_online", "target")
    )
  ) return false;
  return (
    storeId?.trim().toLowerCase() === "online" ||
    isOnlineOnlyRetailer(normalizedRetailer)
  );
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const isValidCode = (value: string | null | undefined): boolean =>
  !!value && value !== "0" && value.trim() !== "";

export type ChartDataPoint = {
  date: number;
  price: number;
  inventory: number;
};

export const getProductDetailEntitlements = (input: {
  isFreeUser: boolean;
  isOnline: boolean;
}) => ({
  canLoadDetail: !input.isFreeUser || input.isOnline,
  canViewHistory: !input.isFreeUser || input.isOnline,
  canViewAdvancedInsights: !input.isFreeUser,
});

export const getProductDetailIdentifier = (
  product: { productKey?: string | null; sku?: string | null; upc?: string | null },
  retailer: string
) => {
  const sku = product.sku?.trim() || "";
  const upc = product.upc?.trim() || "";
  const retailerIdentifier = retailer === "bestbuy" || retailer === "costco"
    ? sku || upc
    : upc || sku;
  // Browser browse responses carry an opaque productKey for outbound-link
  // protection, but authenticated detail lookups are keyed by UPC/SKU. Keep
  // productKey as the fallback for public cards, which intentionally omit the
  // raw retailer identifiers.
  return retailerIdentifier || product.productKey?.trim() || "";
};

export const buildPriceHistoryData = (product?: Product | null) => {
  if (!product || !product.storePrice || !product.lastUpdates) return [];
  return product.storePrice
    .map((price, index) => ({
      date: product.lastUpdates[index],
      price,
      inventory: product.salesFloor?.[index] || 0,
    }))
    .filter((item) => item.date)
    .sort((a, b) => b.date - a.date);
};

export const getCurrentPrice = (product?: Product | null) => {
  return getLatestValue(product?.storePrice, 0);
};

export const getCurrentInventory = (product?: Product | null) => {
  return getLatestValue(product?.salesFloor, 0);
};

export const getSavingsFromOnlinePrice = (
  onlinePrice: number | undefined,
  currentPrice: number
) => {
  if (!onlinePrice || onlinePrice <= currentPrice) return 0;
  return onlinePrice - currentPrice;
};

export const getProductUrl = (input: {
  url?: string | null;
  retailer: string;
  storeId?: string | null;
}) => {
  return getRetailerProductUrl({
    url: input.url,
    retailer: input.retailer,
    storeId: input.storeId,
  });
};

export const buildStoreResult = (input: {
  product?: Product | null;
  currentPrice: number;
  currentInventory: number;
}): StoreProductResult | null => {
  const { product, currentPrice, currentInventory } = input;
  if (!product) return null;

  const name = product.name?.trim();
  const sku = product.sku?.trim();
  const upc = product.upc?.trim();

  if (!name || !upc) return null;

  return {
    storeId: product.storeId || "",
    storeAddress: `Store ${product.storeId || "Unknown"}`,
    name,
    barcode: upc,
    storePrice: currentPrice,
    onlinePrice: product.onlinePrice || 0,
    msrp: product.msrp || 0,
    image: product.image || "",
    mainCategory: product.category || "",
    categoryPath: product.category || "",
    categoryPathId: "",
    brand: product.category || "",
    inventory: currentInventory,
    backroom:
      product.backroom && product.backroom.length > 0
        ? product.backroom[product.backroom.length - 1]
        : 0,
    status: product.inStock ? "IN_STOCK" : "OUT_OF_STOCK",
    aisles: product.aisle || "",
    sku: sku || upc,
    error: undefined,
  };
};

export const getDiscountPercentForProduct = (
  msrp: number,
  currentPrice: number
) => {
  return getDiscountPercent(msrp, currentPrice);
};

export const buildProductAnnouncementPayload = (input: {
  product: Product;
  currentPrice: number;
  discountPercent: number;
  affiliateLink: string;
  imageUrl?: string;
}) => {
  const { product, currentPrice, discountPercent, affiliateLink, imageUrl } = input;
  const msrp = product.msrp || 0;
  const name = product.name.trim();
  const title =
    discountPercent > 0
      ? `🔥 ${discountPercent}% off — ${formatCurrency(currentPrice)}`
      : `${formatCurrency(currentPrice)} — ${name.slice(0, 80)}`;
  const priceLine =
    msrp > currentPrice
      ? `Now ${formatCurrency(currentPrice)} (was ${formatCurrency(msrp)})`
      : `Now ${formatCurrency(currentPrice)}`;
  const body = [name, priceLine, affiliateLink].join("\n\n");
  return { title, body, imageUrl };
};
